/**
 * AI Usage Analytics API Client
 *
 * Provides methods for fetching AI usage statistics and analytics data
 */

import { api } from './client';
import type {
  AIUsageDashboard,
  AIUsageHistoryResponse,
  TimelineDataPoint,
  AIUsageStats,
  TimelineParams,
  HistoryParams,
  StatsParams
} from '@/types/aiUsage';
import type { ApiResponse } from '@/types';

/**
 * API endpoints for AI usage analytics
 * Note: /api/v1 prefix is handled by the API client's baseURL
 */
const ENDPOINTS = {
  DASHBOARD: '/ai/usage/dashboard',
  HISTORY: '/ai/usage/history',
  TIMELINE: '/ai/usage/timeline',
  STATS: '/ai/usage/stats',
  HEALTH: '/ai/usage/health'
} as const;

/**
 * Get AI usage dashboard metrics
 *
 * Returns comprehensive dashboard data including:
 * - Summary metrics (total generations, success rate, costs, tokens)
 * - Feature usage breakdown
 * - Timeline data for charts (last 30 days)
 * - Recent activity (last 10 requests)
 *
 * @returns Promise<ApiResponse<AIUsageDashboard>>
 */
export async function getAIUsageDashboard(): Promise<ApiResponse<AIUsageDashboard>> {
  return api.get<AIUsageDashboard>(ENDPOINTS.DASHBOARD);
}

/**
 * Get paginated AI usage history
 *
 * Supports filtering by date range, request type, success status, and provider
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<AIUsageHistoryResponse>
 */
export async function getAIUsageHistory(
  params?: HistoryParams
): Promise<AIUsageHistoryResponse> {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.requestType) queryParams.append('requestType', params.requestType);
    if (params.success !== undefined) queryParams.append('success', params.success.toString());
    if (params.provider) queryParams.append('provider', params.provider);
  }

  const url = queryParams.toString()
    ? `${ENDPOINTS.HISTORY}?${queryParams.toString()}`
    : ENDPOINTS.HISTORY;

  const response = await api.get<AIUsageHistoryResponse>(url);
  if (response.data) {
    return response.data;
  }

  return {
    success: response.success,
    data: [],
    meta: {
      pagination: {
        page: 1,
        limit: 0,
        totalRecords: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      filters: {
        requestType: params?.requestType,
        success: params?.success === undefined ? undefined : String(params.success),
        provider: params?.provider,
        dateRange: {
          start: params?.startDate,
          end: params?.endDate,
        },
      },
      timestamp: new Date().toISOString(),
    },
  };
}

/**
 * Get time-series AI usage data for charts
 *
 * Returns timeline data grouped by specified timeframe
 *
 * @param params - Timeline parameters (timeframe, days, projectId)
 * @returns Promise<ApiResponse<TimelineDataPoint[]>>
 */
export async function getAIUsageTimeline(
  params?: TimelineParams
): Promise<ApiResponse<TimelineDataPoint[]>> {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.timeframe) queryParams.append('timeframe', params.timeframe);
    if (params.days) queryParams.append('days', params.days.toString());
    if (params.projectId) queryParams.append('projectId', params.projectId);
  }

  const url = queryParams.toString()
    ? `${ENDPOINTS.TIMELINE}?${queryParams.toString()}`
    : ENDPOINTS.TIMELINE;

  return api.get<TimelineDataPoint[]>(url);
}

/**
 * Get aggregated usage statistics
 *
 * Returns statistics grouped by provider, model, and request type
 *
 * @param params - Stats parameters (timeframe)
 * @returns Promise<ApiResponse<AIUsageStats[]>>
 */
export async function getAIUsageStats(
  params?: StatsParams
): Promise<ApiResponse<AIUsageStats[]>> {
  const queryParams = new URLSearchParams();

  if (params?.timeframe) {
    queryParams.append('timeframe', params.timeframe);
  }

  const url = queryParams.toString()
    ? `${ENDPOINTS.STATS}?${queryParams.toString()}`
    : ENDPOINTS.STATS;

  return api.get<AIUsageStats[]>(url);
}

/**
 * Health check for AI usage analytics service
 *
 * @returns Promise<ApiResponse<{ status: string }>>
 */
export async function checkAIUsageHealth(): Promise<ApiResponse<{ status: string }>> {
  return api.get<{ status: string }>(ENDPOINTS.HEALTH);
}

/**
 * Format cost for display
 *
 * @param cost - Cost in dollars
 * @returns Formatted cost string
 */
export function formatCost(cost: number): string {
  if (cost < 0.01) {
    return `$${cost.toFixed(4)}`;
  }
  return `$${cost.toFixed(2)}`;
}

/**
 * Format number with commas
 *
 * @param num - Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('en-US');
}

/**
 * Format tokens with K/M suffix
 *
 * @param tokens - Number of tokens
 * @returns Formatted token string
 */
export function formatTokens(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
}

/**
 * Format response time
 *
 * @param ms - Response time in milliseconds
 * @returns Formatted time string
 */
export function formatResponseTime(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.round(ms)}ms`;
}

/**
 * Get color for success rate
 *
 * @param rate - Success rate percentage (0-100)
 * @returns Tailwind color class
 */
export function getSuccessRateColor(rate: number): string {
  if (rate >= 95) return 'text-green-600';
  if (rate >= 80) return 'text-yellow-600';
  return 'text-red-600';
}

/**
 * Get color for cache hit rate
 *
 * @param rate - Cache hit rate percentage (0-100)
 * @returns Tailwind color class
 */
export function getCacheHitRateColor(rate: number): string {
  if (rate >= 80) return 'text-green-600';
  if (rate >= 50) return 'text-yellow-600';
  return 'text-gray-600';
}
